import React from "react";

import { useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { CROP_TYPE_FCPO, FieldCropRecommendation } from "models";

import { Card, CropRotationRecommendationCard, Flex, Icon, Tag } from "components";

export type MonthlyEmptyCropCardProps = {
  cropType: CROP_TYPE_FCPO;
  payout?: number;
  currency?: CurrencyCode;
  year?: number;
  recommendations?: {
    fieldId: string;
    harvestEndDate: string;
    seedingStartDate: string;
    cropRecommendations: FieldCropRecommendation[];
  };
  isSelected?: boolean;
  isNarrow?: boolean;
  isRequired?: boolean;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  dataTestId?: string;
  isFetching: boolean;
  handleClick?: () => void;
  handleFocus?: () => void;
  handleBlur?: () => void;
  simulationId?: string;
  fieldSize?: number;
};

export const MonthlyEmptyCropCard = ({
  cropType,
  payout,
  currency,
  year,
  recommendations,
  isSelected,
  isRequired,
  isBaseline,
  dataTestId,
  handleClick,
  handleFocus,
  handleBlur,
  simulationId,
  fieldSize,
}: MonthlyEmptyCropCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  const renderCrop = (text: string) => (
    <Card
      className={`main-crop-empty-card layout ${isRequired ? "mandatory" : "optional"} ${isSelected ? "selected" : ""} `}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid={`main-crop-empty-card-${dataTestId}`}
    >
      <Flex align="center" justify="space-between" gap="0.5rem">
        <Flex align="center" justify="flex-start" gap="0.5rem">
          {!isRequired && <Icon dataTestId={`main-crop-empty-card-${dataTestId}-plus-icon`} className="add-icon inline icon" iconName="klim/plus" />}
          <p className="add-text">{text}</p>
        </Flex>
        {isBaseline && <Tag className="baseline-tag" colour="grey" text={t("cropCard.baseline")} />}
      </Flex>
    </Card>
  );

  const renderRecommendations = () => (
    <CropRotationRecommendationCard
      cropType={cropType}
      payout={payout}
      currency={currency}
      year={year}
      recommendations={recommendations}
      isSelected={isSelected}
      dataTestId={dataTestId}
      simulationId={simulationId}
      fieldSize={fieldSize}
    />
  );

  if (cropType === CROP_TYPE_FCPO.MAIN) {
    return renderCrop(t("cropCard.addMainCrop"));
  }

  if (cropType === CROP_TYPE_FCPO.UNDERSOWING && !recommendations) {
    return renderCrop(t("cropCard.addUndersowing"));
  }

  if (cropType === CROP_TYPE_FCPO.UNDERSOWING && recommendations) {
    return renderRecommendations();
  }

  if (cropType === CROP_TYPE_FCPO.COVER && !recommendations) {
    return renderCrop(t("cropCard.addCover"));
  }

  if (cropType === CROP_TYPE_FCPO.COVER && recommendations) {
    return renderRecommendations();
  }
};
